import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  OnboardingDetail,
  OnboardingFields,
  OnboardingListResponse,
  OnboardingStatus,
  PushResult,
} from './onboarding-types'

const BASE = '/api/product-onboarding'
const KEY = ['product-onboarding'] as const

export function useOnboardingList(filters: { status?: OnboardingStatus | ''; q?: string; includeClosed?: boolean }) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.q) params.set('q', filters.q)
  if (filters.includeClosed) params.set('include_closed', 'true')
  const qs = params.toString()
  return useQuery({
    queryKey: [...KEY, 'list', qs],
    queryFn: () => jsonFetch<OnboardingListResponse>(`${BASE}${qs ? `?${qs}` : ''}`),
    staleTime: 15_000,
  })
}

export function useOnboardingDetail(id: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', id],
    queryFn: () => jsonFetch<OnboardingDetail>(`${BASE}/${id}`),
    enabled: !!id,
    staleTime: 5_000,
    // Keep polling while the sample CofA / SDS are being read.
    refetchInterval: (q) => {
      const s = q.state.data?.extraction_state
      return s === 'pending' || s === 'running' ? 4_000 : false
    },
  })
}

function useInvalidate() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: KEY })
}

export function useCreateOnboarding() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (body: Partial<OnboardingFields> & { odoo_product_tmpl_id?: number }) =>
      jsonFetch<OnboardingDetail>(BASE, { method: 'POST', body }),
    onSuccess: invalidate,
  })
}

export function usePatchOnboarding() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: { id: string; values: Partial<OnboardingFields> }) =>
      jsonFetch<{ changed: string[]; status: OnboardingStatus }>(`${BASE}/${vars.id}`, {
        method: 'PATCH',
        body: vars.values,
      }),
    onSuccess: invalidate,
  })
}

function useAction<V>(path: (vars: V) => string, body?: (vars: V) => unknown) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: V) =>
      jsonFetch<{ status?: OnboardingStatus; push?: PushResult | null } & Partial<PushResult>>(path(vars), {
        method: 'POST',
        body: body ? body(vars) : undefined,
      }),
    onSuccess: invalidate,
  })
}

export const useSubmitOnboarding = () => useAction<string>((id) => `${BASE}/${id}/submit`)

export const useSignOnboarding = () =>
  useAction<{ id: string; kind: 'regulatory' | 'commercial'; note?: string; acknowledgeBelowCost?: boolean }>(
    (v) => `${BASE}/${v.id}/sign`,
    (v) => ({ kind: v.kind, note: v.note || null, acknowledge_below_cost: !!v.acknowledgeBelowCost }),
  )

export const useReturnOnboarding = () =>
  useAction<{ id: string; reason: string }>((v) => `${BASE}/${v.id}/return`, (v) => ({ reason: v.reason }))

export const useCancelOnboarding = () =>
  useAction<{ id: string; reason: string }>((v) => `${BASE}/${v.id}/cancel`, (v) => ({ reason: v.reason }))

export const usePushOnboarding = () => useAction<string>((id) => `${BASE}/${id}/push`)

export const useReopenOnboarding = () =>
  useAction<{ id: string; reason: string }>((v) => `${BASE}/${v.id}/reopen`, (v) => ({ reason: v.reason }))

export const useReextractOnboarding = () => useAction<string>((id) => `${BASE}/${id}/extract`)

export function useUploadOnboardingDocument() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: (vars: { id: string; file: File; docTypeKey: string; versionLabel?: string; issuedOn?: string; expiresOn?: string }) => {
      const form = new FormData()
      form.set('file', vars.file)
      form.set('doc_type_key', vars.docTypeKey)
      if (vars.versionLabel) form.set('version_label', vars.versionLabel)
      if (vars.issuedOn) form.set('issued_on', vars.issuedOn)
      if (vars.expiresOn) form.set('expires_on', vars.expiresOn)
      return jsonFetch<{ id: number; unchanged: boolean }>(`${BASE}/${vars.id}/documents`, {
        method: 'POST',
        rawBody: form,
      })
    },
    onSuccess: invalidate,
  })
}

export const useArchiveOnboardingDocument = () =>
  useAction<{ id: string; docId: number }>((v) => `${BASE}/${v.id}/documents/${v.docId}/archive`)

export interface LookupItem {
  id: number
  name: string
  category?: string | null
}

export interface WaitingItem {
  id: string
  reference: string
  name: string | null
  status: OnboardingStatus
  why: string
}

/** Requests waiting for the signed-in person (their sign-off, their returned drafts). */
export function useWaitingForMe() {
  return useQuery({
    queryKey: [...KEY, 'waiting'],
    queryFn: () => jsonFetch<{ count: number; items: WaitingItem[] }>(`${BASE}/waiting`),
    staleTime: 30_000,
    refetchInterval: 60_000,
  })
}

export interface UomOption {
  id: number
  name: string
  label: string
}

/** The units a new product may use (kg / lb), for the dropdown. */
export function useUomOptions() {
  return useQuery({
    queryKey: [...KEY, 'uom-options'],
    queryFn: () => jsonFetch<{ items: UomOption[] }>(`${BASE}/uom-options`),
    staleTime: 10 * 60_000,
  })
}

export interface HsSuggestion {
  hts_code: string
  description: string
  general_duty: string | null
  units: string[]
  url: string
  reasoning: string
}

export interface HsSuggestionsResult {
  suggestions?: HsSuggestion[]
  rejected?: string[]
  caveat?: string | null
  error?: string
}

/** Suggested US HTS codes, each confirmed in the official schedule. Nothing is saved. */
export function useHsCodeSuggestions() {
  return useMutation({
    mutationFn: (id: string) => jsonFetch<HsSuggestionsResult>(`${BASE}/${id}/hs-code-suggestions`, { method: 'POST' }),
  })
}

export function useLookup(kind: 'uom' | 'categories', q: string) {
  return useQuery({
    queryKey: [...KEY, 'lookup', kind, q],
    queryFn: () =>
      jsonFetch<{ items: LookupItem[] }>(`${BASE}/search/${kind}${q ? `?q=${encodeURIComponent(q)}` : ''}`),
    staleTime: 60_000,
  })
}
